import type { Workspace } from './model'
import type { WorkspaceRepository } from './repository'
import { isPracticeChapter, safePracticeReturnRoute, TUTORIAL_VERSION, type TutorialRun } from './practice'

function validateRun(run:TutorialRun) {
  if(run.version!==TUTORIAL_VERSION)throw new Error('This practice version has changed. Start a new run from User guide.')
  if(!isPracticeChapter(run.chapterId)||!Number.isInteger(run.stepIndex)||run.stepIndex<0||!Array.isArray(run.completedSteps)||run.completedSteps.some(value=>typeof value!=='string')||!Array.isArray(run.evidence))throw new Error('Practice progress is unavailable.')
  safePracticeReturnRoute(run.returnRoute)
}

/** Read and validate both destinations before changing the repository's bound workspace. */
export async function resumePracticeWorkspace(repository:WorkspaceRepository,id:string,returnWorkspaceId?:string):Promise<{workspace:Workspace;run:TutorialRun}> {
  if(!repository.getTutorialRun||!repository.switchWorkspace)throw new Error('Practice is unavailable in this repository.')
  const run=await repository.getTutorialRun(id)
  if(run.id!==id)throw new Error('Practice progress targets another workspace.')
  validateRun(run)
  if(returnWorkspaceId&&run.returnWorkspaceId!==returnWorkspaceId)throw new Error('Practice progress targets another workspace.')
  const candidate=await repository.load(run.workspaceId)
  if(candidate.id!==run.workspaceId||candidate.kind!=='practice'||candidate.practice?.runId!==run.id||candidate.practice?.tutorialVersion!==run.version||candidate.practice?.returnWorkspaceId!==run.returnWorkspaceId)throw new Error('Practice progress targets another workspace.')
  const original=await repository.load(run.returnWorkspaceId)
  if(original.kind==='practice')throw new Error('The original workspace is unavailable. Choose another workspace before practicing.')
  return {workspace:await repository.switchWorkspace(candidate.id!),run}
}

/** Pin operations first: an invalid persisted practice pointer must never choose its fallback. */
export async function loadInitialPracticeWorkspace(repository:WorkspaceRepository,id?:string|null):Promise<{workspace:Workspace;run:TutorialRun|null;error?:string}> {
  const initial=await repository.load()
  const original=initial.kind==='practice'&&initial.practice?await repository.load(initial.practice.returnWorkspaceId):initial
  if(original.kind==='practice')throw new Error('The original workspace is unavailable. Choose another workspace before practicing.')
  if(!id)return {workspace:initial.kind==='practice'&&repository.switchWorkspace?await repository.switchWorkspace(original.id!):original,run:null}
  try {return await resumePracticeWorkspace(repository,id)}
  catch(issue) {
    const workspace=repository.switchWorkspace?await repository.switchWorkspace(original.id!):await repository.load(original.id)
    return {workspace,run:null,error:issue instanceof Error?issue.message:'Practice could not resume. Your operations workspace is unchanged.'}
  }
}
